// Shared building blocks for bespoke private pitches. Content comes from airline.content.
import { useRef } from "react";
import {
  Users, Network, ShieldCheck, Clapperboard, BookOpenCheck, Palette, Megaphone, BarChart3, Target,
  Film, Gift, Layers, ClipboardList, Tag, Search, Package, FileSignature, RefreshCw, BadgeCheck,
  Inbox, Wand2, Plane, Play, FileCheck2, Tv, Headphones, Sparkles,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useAirline } from "../../context/AirlineContext";

const PAL_ICONS = {
  Users, Network, ShieldCheck, Clapperboard, BookOpenCheck, Palette, Megaphone, BarChart3, Target,
  Film, Gift, Layers, ClipboardList, Tag, Search, Package, FileSignature, RefreshCw, BadgeCheck,
  Inbox, Wand2, Plane, Play, FileCheck2, Tv, Headphones,
};

export const PalIcon = ({ name, ...props }) => {
  const Icon = PAL_ICONS[name] || Sparkles;
  return <Icon aria-hidden="true" {...props} />;
};

export const FOCUS = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8EA0FF]";

export const fmt2 = (n) =>
  Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtKM = (n) => {
  const v = Number(n);
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(2)}K`;
  return v.toFixed(2);
};

export const usePal = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  return {
    airline,
    isDark,
    c: airline?.content || {},
    brand: airline?.brandColor || "#5B6EE1",
    ink: isDark ? "text-white" : "text-[#1d1d1f]",
    muted: isDark ? "text-white/70" : "text-black/65",
    dim: isDark ? "text-white/55" : "text-black/55",
    hair: isDark ? "border-white/10" : "border-black/10",
    surface: isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10",
  };
};

export const PalHead = ({ eyebrow, title, accentLine, lead, action, testid, wide }) => {
  const { brand, ink, muted } = usePal();
  return (
    <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-6" data-testid={testid}>
      <div>
        <p className="k-eyebrow" style={{ color: brand }}>{eyebrow}</p>
        <h2 className={`k-h2 mt-3 ${wide ? "lg:whitespace-nowrap" : "max-w-[18ch]"} ${ink}`}>
          {title}
          {accentLine && <span className="block" style={{ color: brand }}>{accentLine}</span>}
        </h2>
        {lead && <p className={`k-lead mt-5 max-w-[60ch] ${muted}`}>{lead}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </header>
  );
};

export const SubHead = ({ children, id }) => {
  const { dim } = usePal();
  return <h3 id={id} className={`k-eyebrow ${dim}`}>{children}</h3>;
};

export const Pill = ({ children, color, testid }) => {
  const { isDark } = usePal();
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ backgroundColor: `${color}${isDark ? "26" : "1f"}`, color: isDark ? color : "#1d1d1f" }}
    >
      {!isDark && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />}
      {children}
    </span>
  );
};

// Accessible tab list with arrow-key navigation.
export const PalTabs = ({ tabs, active, onChange, idPrefix }) => {
  const { isDark, brand } = usePal();
  const refs = useRef([]);
  const onKey = (e, i) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    e.stopPropagation();
    const next = (i + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    onChange(next);
    refs.current[next]?.focus();
  };
  return (
    <div role="tablist" aria-label="Sections" className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
      {tabs.map((t, i) => {
        const on = i === active;
        return (
          <button
            key={t}
            ref={(el) => (refs.current[i] = el)}
            role="tab"
            id={`${idPrefix}-tab-${i}`}
            aria-selected={on}
            aria-controls={`${idPrefix}-panel-${i}`}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(i)}
            onKeyDown={(e) => onKey(e, i)}
            data-testid={`${idPrefix}-tab-${i}`}
            className={`whitespace-nowrap h-9 px-4 rounded-full text-[13px] font-semibold transition-colors ${FOCUS} ${
              on ? "text-white" : isDark ? "text-white/70 hover:bg-white/[0.08]" : "text-black/65 hover:bg-black/[0.05]"
            }`}
            style={on ? { backgroundColor: brand } : undefined}
          >
            {String.fromCharCode(65 + i)}. {t}
          </button>
        );
      })}
    </div>
  );
};

export const TabPanel = ({ idPrefix, index, children }) => (
  <div
    role="tabpanel"
    id={`${idPrefix}-panel-${index}`}
    aria-labelledby={`${idPrefix}-tab-${index}`}
    data-testid={`${idPrefix}-panel-${index}`}
    className="mt-8"
    style={{ animation: "fadeIn 0.3s ease-out" }}
  >
    {children}
  </div>
);

export const NoteList = ({ items }) => {
  const { muted, brand } = usePal();
  return (
    <ul className={`space-y-2 text-[14px] leading-relaxed ${muted}`}>
      {items.map((n) => (
        <li key={n} className="flex gap-2.5">
          <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: brand }} />
          <span>{n}</span>
        </li>
      ))}
    </ul>
  );
};
